import React, { useState } from "react";
import { PRODUCTS } from "../data/copperx";
import { ArrowUpRight } from "lucide-react";

export default function Products({ onQuoteClick }) {
  const [active, setActive] = useState(PRODUCTS[0].id);
  const current = PRODUCTS.find((p) => p.id === active);

  return (
    <section
      id="products"
      data-testid="products-section"
      className="bg-[#F8F9FA] py-24 md:py-32 border-y border-[#E2E8F0]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="section-rule" />
              <span className="text-[#B85C38] text-xs tracking-[0.3em] uppercase font-semibold">
                Product Catalogue
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight text-[#0F172A] leading-[1.05] max-w-3xl">
              Curated for outstanding{" "}
              <span className="text-[#B85C38]">quality &amp; durability.</span>
            </h2>
          </div>
          <p className="text-[#475569] text-base md:text-lg max-w-md">
            Five core product families covering the full HVAC-R copper piping
            spectrum — from soft-coil VRF runs to large-bore chiller mains and
            aluminium finstock.
          </p>
        </div>

        {/* Tab strip */}
        <div className="flex flex-wrap gap-0 border-b border-[#E2E8F0] mb-12 overflow-x-auto" role="tablist">
          {PRODUCTS.map((p) => (
            <button
              key={p.id}
              role="tab"
              aria-selected={active === p.id}
              onClick={() => setActive(p.id)}
              data-testid={`product-tab-${p.id}`}
              className={`px-5 md:px-7 py-4 text-sm md:text-base font-medium tracking-wide whitespace-nowrap transition-colors border-b-2 -mb-px ${
                active === p.id
                  ? "border-[#B85C38] text-[#0F172A]"
                  : "border-transparent text-[#475569] hover:text-[#0F172A]"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>

        {/* Active product detail */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden bg-[#18181B]">
              <img
                src={current.image}
                alt={current.name}
                className="w-full h-[460px] object-cover opacity-95"
                data-testid={`product-image-${current.id}`}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                <div className="text-[#E19672] text-xs tracking-[0.3em] uppercase font-semibold mb-1">
                  {current.subtitle}
                </div>
                <div className="text-white text-2xl font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                  {current.name}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h4 className="text-xs tracking-[0.3em] uppercase text-[#B85C38] font-semibold mb-4">
                Applications
              </h4>
              <div className="flex flex-wrap gap-2">
                {current.apps.map((a) => (
                  <span
                    key={a}
                    className="bg-white border border-[#E2E8F0] text-[#0F172A] text-xs font-mono px-3 py-2"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="text-[#475569] text-lg leading-relaxed mb-10">
              {current.desc}
            </p>

            <div className="space-y-10">
              {current.specs.map((spec) => (
                <div key={spec.title} data-testid={`spec-table-${current.id}-${spec.title.split(' ')[0].toLowerCase()}`}>
                  <h4 className="text-[#0F172A] text-lg font-medium mb-4 flex items-center gap-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                    <span className="w-2 h-2 bg-[#B85C38]" />
                    {spec.title}
                  </h4>
                  <div className="overflow-x-auto border border-[#E2E8F0] bg-white">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-[#18181B] text-white">
                          {spec.cols.map((c) => (
                            <th
                              key={c}
                              className="text-left p-3 md:p-4 font-semibold text-xs tracking-wider uppercase"
                            >
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {spec.rows.map((row, i) => (
                          <tr
                            key={`${spec.title}-row-${row.join("|")}-${i}`}
                            className="border-b border-[#E2E8F0] last:border-0 hover:bg-[#F8F9FA] transition-colors"
                          >
                            {row.map((cell, j) => (
                              <td
                                key={`${spec.cols[j]}-${cell}-${j}`}
                                className="p-3 md:p-4 font-mono text-[#475569]"
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={onQuoteClick}
              data-testid={`product-quote-button-${current.id}`}
              className="mt-10 inline-flex items-center gap-3 bg-[#18181B] hover:bg-[#27272A] text-white px-7 py-4 font-medium tracking-wide transition-colors group"
            >
              Request quote for {current.name}
              <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
